import { useState, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
    createChatRequest,
    deleteChatRequest,
    getChatsRequest,
    resetChat,
    saveMessageRequest,
    updateChatRequest,
} from '../../../../api/hooks/chat/chatApi'

// Simplified chat hook without persistence
export const useChats = (isPublic = false) => {
    const [currentChat, setCurrentChat] = useState(null)
    const [chatsLimit, setChatsLimit] = useState(20)
    const [chatsCursor, setChatsCursor] = useState(0)
    const [deletingChatIDs, setDeletingChatIDs] = useState(new Set()) // Track which chats are being deleted
    const userID = useSelector((state) => state.auth?.userID)
    const workspaceID = useSelector((state) => state.auth?.workspaceID)
    const queryClient = useQueryClient()

    const { mutate: reset, isPending: resetPending } = useMutation({
        mutationFn: ({ chatID, inboxID }) => resetChat(chatID, inboxID),
        onSuccess: (response, variables) => {
            variables?.onSuccess(response)
        },
        onError: (error, variables) => {
            variables?.onError(error)
        },
    })

    const { mutate: createChat, isPending: chatCreationPending } = useMutation({
        mutationFn: ({ title }) => createChatRequest(title),
        onMutate: async ({ title }) => {
            // Cancel any outgoing refetches
            await queryClient.cancelQueries({
                queryKey: ['chats', workspaceID, userID, chatsLimit, chatsCursor],
            })

            // Snapshot the previous value
            const previousChats = queryClient.getQueryData([
                'chats',
                workspaceID,
                userID,
                chatsLimit,
                chatsCursor,
            ])

            // Create optimistic chat entry
            const optimisticChat = {
                chatID: `temp-${Date.now()}`, // Temporary ID
                title,
                createdAt: new Date().toISOString(),
                lastMessageAt: new Date().toISOString(),
                messageCount: 0,
                isOptimistic: true, // Flag to identify optimistic entries
            }

            // Optimistically add the new chat to the beginning of the list
            queryClient.setQueryData(
                ['chats', workspaceID, userID, chatsLimit, chatsCursor],
                (old) => {
                    if (!old) return { chats: [optimisticChat] }
                    return {
                        ...old,
                        chats: [optimisticChat, ...old.chats],
                    }
                }
            )

            // Return a context object with the snapshotted value and optimistic chat
            return { previousChats, optimisticChat }
        },
        onError: (err, variables, context) => {
            // If the mutation fails, roll back
            if (context?.previousChats) {
                queryClient.setQueryData(
                    ['chats', workspaceID, userID, chatsLimit, chatsCursor],
                    context.previousChats
                )
            }
            variables?.onError(err)
        },
        onSuccess: (response, variables, context) => {
            // Replace the optimistic entry with the real one from the server
            queryClient.setQueryData(
                ['chats', workspaceID, userID, chatsLimit, chatsCursor],
                (old) => {
                    if (!old) return old
                    return {
                        ...old,
                        chats: old.chats.map((chat) => {
                            if (chat.isOptimistic && chat.title === variables.title) {
                                // Preserve optimistic values if server doesn't provide them
                                return {
                                    ...response,
                                    isOptimistic: false,
                                    // Keep optimistic timestamps if server response doesn't have them
                                    createdAt: response.createdAt || chat.createdAt,
                                    lastMessageAt: response.lastMessageAt || chat.lastMessageAt,
                                    messageCount: response.messageCount ?? chat.messageCount,
                                }
                            }
                            return chat
                        }),
                    }
                }
            )
            variables?.onSuccess(response)
        },
        onSettled: () => {
            // Always refetch after error or success
            queryClient.invalidateQueries({
                queryKey: ['chats', workspaceID, userID, chatsLimit, chatsCursor],
            })
        },
    })

    const { mutate: deleteChat, isPending: chatDeletePending } = useMutation({
        mutationFn: ({ chatID, userID, workspaceID }) =>
            deleteChatRequest(chatID, userID, workspaceID),
        onMutate: ({ chatID }) => {
            // Add chat to deleting set
            setDeletingChatIDs((prev) => new Set([...prev, chatID]))
        },
        onSuccess: (response, variables) => {
            // If we're deleting the currently selected chat, clear it
            if (currentChat?.chatID === variables.chatID) {
                setCurrentChat(null)
            }

            // Remove from deleting set
            setDeletingChatIDs((prev) => {
                const newSet = new Set(prev)
                newSet.delete(variables.chatID)
                return newSet
            })

            // Refetch chats to get updated list
            queryClient.invalidateQueries({
                queryKey: ['chats', workspaceID, userID, chatsLimit, chatsCursor],
            })

            variables?.onSuccess(response)
        },
        onError: (err, variables) => {
            // Remove from deleting set on error
            setDeletingChatIDs((prev) => {
                const newSet = new Set(prev)
                newSet.delete(variables.chatID)
                return newSet
            })

            variables?.onError(err)
        },
    })

    const { mutate: saveMessage, isPending: saveMessagePending } = useMutation({
        mutationFn: ({ chatID, content }) => saveMessageRequest(chatID, content),
        onMutate: async ({ chatID, content }) => {
            // For now, just track that we're saving
            // In the future, we could add message status tracking here
            return { chatID, content }
        },
        onSuccess: (response, variables) => {
            // Message saved successfully
            variables?.onSuccess && variables.onSuccess(response)
        },
        onError: (error, variables) => {
            // Message save failed - could add retry logic later
            variables?.onError && variables.onError(error)
        },
    })

    const { mutate: updateChat, isPending: updateChatPending } = useMutation({
        mutationFn: ({ chatID, title }) => updateChatRequest(chatID, title),
        onMutate: async ({ chatID, title }) => {
            // Cancel any outgoing refetches
            await queryClient.cancelQueries({
                queryKey: ['chats', workspaceID, userID, chatsLimit, chatsCursor],
            })

            // Snapshot the previous value
            const previousChats = queryClient.getQueryData([
                'chats',
                workspaceID,
                userID,
                chatsLimit,
                chatsCursor,
            ])

            // Optimistically update the chat title
            queryClient.setQueryData(
                ['chats', workspaceID, userID, chatsLimit, chatsCursor],
                (old) => {
                    if (!old) return old
                    return {
                        ...old,
                        chats: old.chats.map((chat) =>
                            chat.chatID === chatID ? { ...chat, title } : chat
                        ),
                    }
                }
            )

            // Return a context object with the snapshotted value
            return { previousChats }
        },
        onError: (err, variables, context) => {
            // If the mutation fails, roll back
            if (context?.previousChats) {
                queryClient.setQueryData(
                    ['chats', workspaceID, userID, chatsLimit, chatsCursor],
                    context.previousChats
                )
            }
            variables?.onError(err)
        },
        onSuccess: (response, variables) => {
            variables?.onSuccess(response)
        },
        onSettled: () => {
            // Always refetch after error or success
            queryClient.invalidateQueries({
                queryKey: ['chats', workspaceID, userID, chatsLimit, chatsCursor],
            })
        },
    })

    const {
        data: chats,
        isLoading: loadingChats,
        refetch: refetchChats,
    } = useQuery({
        queryKey: ['chats', workspaceID, userID, chatsLimit, chatsCursor],
        queryFn: () => getChatsRequest(userID, workspaceID, chatsLimit, chatsCursor),
        enabled: !!(workspaceID && userID && !isPublic), // Don't fetch chats for demo users
    })

    const createNewChat = useCallback(() => {
        // Use temporary ID that will be replaced when backend responds
        const tempChatId = `temp-${Date.now()}`

        const newChat = {
            chatID: tempChatId,
            title: 'Chat',
            messages: [],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            isTemporary: true, // Flag to identify temporary chats
        }

        setCurrentChat(newChat)
        return newChat
    }, [])

    const updateChatTitle = useCallback((chatID, title) => {
        setCurrentChat((prev) => {
            if (!prev || prev.chatID !== chatID) return prev

            return {
                ...prev,
                title,
                updatedAt: new Date().toISOString(),
            }
        })
    }, [])

    const clearChat = useCallback(() => {
        if (currentChat) {
            setCurrentChat({
                ...currentChat,
                messages: [],
                updatedAt: new Date().toISOString(),
            })
        }
    }, [currentChat])

    const updateCurrentChatID = useCallback((newChatID) => {
        setCurrentChat((prev) => {
            if (!prev || !prev.isTemporary) return prev
            return {
                ...prev,
                chatID: newChatID,
                isTemporary: false, // No longer temporary
                updatedAt: new Date().toISOString(),
            }
        })
    }, [])

    const setCurrentChatFromBackend = useCallback((backendChat) => {
        setCurrentChat({
            chatID: backendChat.chatID,
            title: backendChat.title,
            messages: [], // Start with empty messages for new chat
            createdAt: backendChat.createdAt,
            updatedAt: backendChat.lastMessageAt || backendChat.updatedAt,
            isTemporary: false, // Backend chats are never temporary
        })
    }, [])

    // Helper function to check if a chat is being deleted
    const isChatDeleting = useCallback(
        (chatID) => {
            return deletingChatIDs.has(chatID)
        },
        [deletingChatIDs]
    )

    // Add chat selection functionality
    const selectChat = useCallback(
        (chatID) => {
            if (chatID === null) {
                // Clear current chat to reset to initial state
                setCurrentChat(null)
                return
            }

            const selectedChat = chats?.chats?.find((chat) => chat.chatID === chatID)
            if (selectedChat) {
                setCurrentChat({
                    chatID: selectedChat.chatID,
                    title: selectedChat.title,
                    messages: [], // Messages will be loaded separately
                    createdAt: selectedChat.createdAt,
                    updatedAt: selectedChat.lastMessageAt,
                })
            }
        },
        [chats]
    )

    return {
        // Persistent chats from backend (for authenticated users)
        persistentChats: chats?.chats || [],
        loadingChats,
        refetchChats,

        // Current session chat
        currentChat,
        currentChatID: currentChat?.chatID,

        // Actions
        createNewChat,
        selectChat,
        updateChatTitle,
        clearAllChats: clearChat,
        updateCurrentChatID,
        setCurrentChatFromBackend,

        // Mutations
        createChat,
        deleteChat,
        saveMessage,
        updateChat,
        reset,

        // Loading states
        chatCreationPending,
        chatDeletePending,
        saveMessagePending,
        updateChatPending,
        resetPending,

        // Delete state helpers
        isChatDeleting,
    }
}
